import React, { useRef, useState, useLayoutEffect } from 'react';
import { Transaction } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../utils/formatters';
import { CoinIcon } from './CoinIcon';
import { CoinTxnStat } from './coinTxnStats';
import { TxnRow } from './TxnRow';
import { txTotalInDisplay } from '../../utils/displayAmount';

interface Props {
  stat: CoinTxnStat;
  filteredCoinTxns: Transaction[];   // whole coin set after wallet filter (for subtotals)
  pagedTxns: Transaction[];          // current page slice, newest first
  imageMap: Record<string, string>;
  onEdit: (tx: Transaction) => void;
  onDelete: (id: string) => void;
}

const BRONZE = '#b89968';

// Width of the pane, used to stack the two columns when it gets narrow.
function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.offsetWidth);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

function StatBlock({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="text-right">
      <p className="uppercase" style={{ fontSize: 9, letterSpacing: '0.09em', color: BRONZE }}>{label}</p>
      <p className="num text-[14px] mt-0.5" style={{ color: color ?? 'rgb(var(--sl-100))' }}>{value}</p>
    </div>
  );
}

function Column({ title, dotColor, rows, count, footerLabel, footerValue, footerColor, emptyText, imageMap, onEdit, onDelete }: {
  title: string;
  dotColor: string;
  rows: Transaction[];
  count: number;
  footerLabel: string;
  footerValue: string;
  footerColor?: string;
  emptyText: string;
  imageMap: Record<string, string>;
  onEdit: (tx: Transaction) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div
      className="flex flex-col"
      style={{
        background: 'rgb(var(--overlay) / 0.012)',
        border: '0.5px solid rgb(var(--overlay) / 0.06)',
        borderRadius: 9,
        padding: 11,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />
          <span className="text-[12px] font-semibold" style={{ color: 'rgb(var(--sl-100))' }}>{title}</span>
        </div>
        <span
          className="text-[10px] num px-1.5 py-0.5 rounded-md"
          style={{ color: 'var(--color-sub)', background: 'rgb(var(--overlay) / 0.04)' }}
        >
          {count}
        </span>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-0.5 flex-1">
        {rows.length === 0 ? (
          <p className="text-[11px] py-4 text-center" style={{ color: 'var(--color-sub)' }}>{emptyText}</p>
        ) : (
          rows.map((tx) => (
            <TxnRow key={tx.id} tx={tx} imageMap={imageMap} onEdit={onEdit} onDelete={onDelete} />
          ))
        )}
      </div>

      {/* Footer subtotal */}
      <div
        className="flex items-center justify-between mt-2 pt-2"
        style={{ borderTop: '0.5px solid rgb(var(--overlay) / 0.07)' }}
      >
        <span className="uppercase" style={{ fontSize: 9, letterSpacing: '0.09em', color: BRONZE }}>{footerLabel}</span>
        <span className="num text-[13px] font-medium" style={{ color: footerColor ?? 'rgb(var(--sl-100))' }}>{footerValue}</span>
      </div>
    </div>
  );
}

export function CoinTransactionsView({ stat, filteredCoinTxns, pagedTxns, imageMap, onEdit, onDelete }: Props) {
  const { fmtDisplay, currency, rate } = useCurrency();
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const stacked = width > 0 && width < 720;

  // Column rows come from the current page slice (newest first).
  const buys = pagedTxns.filter((t) => t.type === 'BUY');
  const sells = pagedTxns.filter((t) => t.type === 'SELL');
  const transfers = pagedTxns.filter((t) => t.type === 'TRANSFER');

  // Subtotals + counts reflect the whole wallet-filtered coin set.
  const allBuys = filteredCoinTxns.filter((t) => t.type === 'BUY');
  const allSells = filteredCoinTxns.filter((t) => t.type === 'SELL');
  // Class A: each transaction converted at its OWN date, and inclusive of fees,
  // so these footers agree with the per-row totals above them rather than being a
  // second, differently-converted tally of the same money.
  const invested = allBuys.reduce((s, t) => s + txTotalInDisplay(t, currency.code, rate).value, 0);
  const proceeds = allSells.reduce((s, t) => s + txTotalInDisplay(t, currency.code, rate).value, 0);

  const netColor = stat.netPnl >= 0 ? 'var(--color-positive)' : 'var(--color-negative)';
  const netSign = stat.netPnl >= 0 ? '+' : '−';

  return (
    <div ref={ref}>
      {/* Header card */}
      <div
        className="flex items-center justify-between gap-4 flex-wrap pb-4 mb-4"
        style={{ borderBottom: '0.5px solid rgb(var(--overlay) / 0.08)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <CoinIcon coinId={stat.coinId} symbol={stat.symbol} imageMap={imageMap} size={34} />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold truncate" style={{ color: 'rgb(var(--sl-100))' }}>{stat.name}</p>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--color-sub)' }}>
              {stat.symbol} · {stat.txnCount} transaction{stat.txnCount === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <StatBlock label="Holdings" value={`${formatAmount(stat.amount)} ${stat.symbol}`} />
          <StatBlock label="Avg buy" value={stat.display.avgBuyPrice > 0 ? fmtDisplay(stat.display.avgBuyPrice) : '—'} />
          <StatBlock label="Net P&L" value={`${netSign}${fmtDisplay(Math.abs(stat.display.netPnl))}`} color={netColor} />
        </div>
      </div>

      {/* Buys / Sells columns */}
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: stacked ? '1fr' : 'repeat(2, minmax(0, 1fr))' }}
      >
        <Column
          title="Buys"
          dotColor="#6db981"
          rows={buys}
          count={allBuys.length}
          footerLabel="Invested"
          footerValue={fmtDisplay(invested)}
          emptyText="No buys on this page"
          imageMap={imageMap}
          onEdit={onEdit}
          onDelete={onDelete}
        />
        <Column
          title="Sells"
          dotColor="#c4615e"
          rows={sells}
          count={allSells.length}
          footerLabel="Proceeds"
          footerValue={fmtDisplay(proceeds)}
          footerColor={proceeds > 0 ? 'var(--color-positive)' : undefined}
          emptyText="No sells yet"
          imageMap={imageMap}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>

      {/* Transfers (this coin) — shown below when present on this page */}
      {transfers.length > 0 && (
        <div
          className="mt-3 flex flex-col"
          style={{
            background: 'rgb(var(--overlay) / 0.012)',
            border: '0.5px solid rgb(var(--overlay) / 0.06)',
            borderRadius: 9,
            padding: 11,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRONZE }} />
            <span className="text-[12px] font-semibold" style={{ color: 'rgb(var(--sl-100))' }}>Transfers</span>
          </div>
          <div className="flex flex-col gap-0.5">
            {transfers.map((tx) => (
              <TxnRow key={tx.id} tx={tx} imageMap={imageMap} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
